import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { useProfile } from "@/context/ProfileContext";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import AdSlot from "@/components/AdSlot";

const OCCASIONS = [
  { key: "casual", it: "Casual", en: "Casual" },
  { key: "business", it: "Business", en: "Business" },
  { key: "elegant", it: "Elegante", en: "Elegant" },
  { key: "ceremony", it: "Cerimonia", en: "Ceremony" },
  { key: "wedding", it: "Matrimonio", en: "Wedding" },
  { key: "date", it: "Appuntamento", en: "Date" },
  { key: "summer", it: "Estate", en: "Summer" },
  { key: "winter", it: "Inverno", en: "Winter" },
  { key: "spring", it: "Primavera", en: "Spring" },
  { key: "autumn", it: "Autunno", en: "Autumn" },
];

const GROUP_LABELS = {
  dresses: ["Abiti", "Dresses"], tops: ["Top", "Tops"], bottoms: ["Bottom", "Bottoms"],
  outerwear: ["Capispalla", "Outerwear"], footwear: ["Calzature", "Footwear"], accessories: ["Accessori", "Accessories"],
};

export default function Outfits() {
  const { lang, L } = useI18n();
  const { profile, hasProfile } = useProfile();
  const [occasion, setOccasion] = useState("casual");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.post("/outfits", { profile, occasion }).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [occasion, profile]);

  return (
    <div className="bg-surface min-h-screen">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <p className="text-xs uppercase tracking-[0.3em] text-rose-500 font-semibold">Motore Outfit</p>
        <h1 className="mt-2 font-serif text-4xl sm:text-5xl">Look completi su misura</h1>
        <p className="mt-3 text-charcoal/55 max-w-xl">Combiniamo capi compatibili con la tua armocromia e la tua corporatura per ogni occasione.</p>
        {!hasProfile && <p className="mt-3 text-sm text-rose-500">Suggerimento: <Link to="/quiz" className="underline font-semibold">fai il quiz</Link> per outfit personalizzati sulla tua stagione.</p>}

        <div className="flex gap-2 overflow-x-auto py-6" data-testid="occasion-chips">
          {OCCASIONS.map((o) => (
            <button key={o.key} onClick={() => setOccasion(o.key)} data-testid={`occasion-${o.key}`}
              className={`whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${occasion === o.key ? "bg-charcoal text-white" : "bg-white border border-rose-100 hover:border-rose-300"}`}>
              {lang === "it" ? o.it : o.en}
            </button>
          ))}
        </div>

        {data?.season && (
          <div className="mb-6 inline-flex items-center gap-2 bg-white border border-rose-100 rounded-full px-4 py-2 text-sm">
            <span className="text-charcoal/50">Stagione:</span><span className="font-semibold">{L(data.season, "name")}</span>
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[3/4] rounded-xl bg-white animate-pulse" />)}</div>
        ) : (
          <div className="space-y-10" data-testid="outfit-groups">
            {Object.entries(data?.categories || {}).map(([group, prods]) => (
              prods.length > 0 && (
                <div key={group}>
                  <h2 className="font-serif text-2xl mb-4">{GROUP_LABELS[group]?.[lang === "it" ? 0 : 1] || group}</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {prods.map((p, i) => <ProductCard key={p.sku} product={p} index={i} />)}
                  </div>
                </div>
              )
            ))}
            <AdSlot format="leaderboard" />
          </div>
        )}
      </div>
    </div>
  );
}
